import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Table, Modal, Badge, Form, InputGroup } from "react-bootstrap";
import {
  PersonDash,
  CheckCircle,
  XCircle,
  Eye,
  People,
  Book,
  Trash,
  ExclamationTriangle,
  Search,
  X
} from 'react-bootstrap-icons';

// Import your existing API functions
import {
  getClassesBySchoolId,
  getAssessmentsByStudentId,
  getStudentsForClass,
  unassignAssessmentBatch,
  getAnswerCount
} from "../services/APIClient";

import secondsToTime from "../services/secondsToTime";

function ClassScreenerRemoval({ schoolId, schoolName, onClose }) {
  // State management
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentAssessments, setStudentAssessments] = useState({});
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(null);
  const [availableAssessmentTypes, setAvailableAssessmentTypes] = useState([]);
  const [selectedAssessmentIds, setSelectedAssessmentIds] = useState([]);
  const [assessmentAnswerCounts, setAssessmentAnswerCounts] = useState({}); // keyed by assessment instance ID

  // Search states
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [assessmentSearchTerm, setAssessmentSearchTerm] = useState("");
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [loadingAnswerCounts, setLoadingAnswerCounts] = useState(false);

  // Load classes when component mounts
  useEffect(() => {
    if (schoolId) {
      loadClasses();
    }
  }, [schoolId]);

  // Load student data when class is selected
  useEffect(() => {
    if (selectedClass) {
      loadClassStudents();
    }
  }, [selectedClass]);

  // Load individual student assessments when students are loaded
  useEffect(() => {
    if (classStudents.length > 0) {
      loadStudentAssessments();
    }
  }, [classStudents]);

  // Extract available assessment types when student assessments are loaded
  useEffect(() => {
    if (Object.keys(studentAssessments).length > 0) {
      extractAvailableAssessmentTypes();
      loadAnswerCounts();
    }
  }, [studentAssessments]);

  // Reset search terms and selections when selections change
  useEffect(() => {
    setStudentSearchTerm("");
    setSelectedStudents([]);
    setSelectedAssessmentIds([]);
  }, [selectedClass]);

  useEffect(() => {
    setAssessmentSearchTerm("");
    setSelectedStudents([]);
    setSelectedAssessmentIds([]);
  }, [selectedClass]);

  useEffect(() => {
    // When assessment type filter changes, reset student selections
    setSelectedStudents([]);
    setSelectedAssessmentIds([]);
  }, [selectedAssessmentType]);

  const loadClasses = async () => {
    try {
      setLoading(true);
      console.log('Loading classes for school ID:', schoolId);
      const response = await getClassesBySchoolId(schoolId);
      console.log('Classes loaded:', response);
      setClasses(response);
      setError(null);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError("Failed to load classes for this school");
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async () => {
    try {
      setLoadingStudentData(true);
      console.log('Loading students for class:', selectedClass);

      const response = await getStudentsForClass(selectedClass.id);
      console.log('Students response:', response);

      const students = response.students || response || [];
      setClassStudents(students);
      setSelectedStudents([]);
      setSelectedAssessmentType(null);
      setError(null);
    } catch (err) {
      console.error('Error loading students:', err);
      setError("Failed to load students for this class");
    } finally {
      setLoadingStudentData(false);
    }
  };

  const loadStudentAssessments = async () => {
    try {
      setLoadingAssessments(true);
      console.log('Loading assessments for students:', classStudents);
      const assessmentData = {};

      for (const student of classStudents) {
        try {
          console.log(`Fetching assessments for student ${student.id} (${student.name})`);
          const studentAssessments = await getAssessmentsByStudentId(schoolId, student.id);
          console.log(`Student ${student.name} assessments:`, studentAssessments);

          // Filter for incomplete/pending assessments only
          const pendingAssessments = studentAssessments.filter(assessment => {
            const isIncomplete = assessment.completed !== true &&
              assessment.status !== 'completed' &&
              assessment.completion_status !== 'completed';
            return isIncomplete;
          });

          assessmentData[student.id] = pendingAssessments;
        } catch (err) {
          console.warn(`Failed to load assessments for student ${student.id}:`, err);
          assessmentData[student.id] = [];
        }
      }

      console.log('All student pending assessment data:', assessmentData);
      setStudentAssessments(assessmentData);
    } catch (err) {
      console.error("Failed to load student assessment data:", err);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const loadAnswerCounts = async () => {
    try {
      setLoadingAnswerCounts(true);
      console.log('Loading answer counts for assessments...');
      const answerCounts = {};

      // Get all assessment instance IDs
      const allAssessmentIds = Object.values(studentAssessments)
        .flat()
        .map(assessment => assessment.id);

      // Call your new API for each assessment instance
      for (const instanceId of allAssessmentIds) {
        try {
          console.log(`Fetching answer count for assessment instance: ${instanceId}`);
          const response = await getAnswerCount(instanceId);
          
          // Extract the answerCount from the response object
          const count = response?.answerCount || 0;
          answerCounts[instanceId] = count;
          console.log(`Assessment ${instanceId} has ${count} answers`);
        } catch (err) {
          console.warn(`Failed to load answer count for assessment ${instanceId}:`, err);
          answerCounts[instanceId] = 0;
        }
      }

      console.log('All answer counts loaded:', answerCounts);
      setAssessmentAnswerCounts(answerCounts);
    } catch (err) {
      console.error("Failed to load answer counts:", err);
    } finally {
      setLoadingAnswerCounts(false);
    }
  };

  const extractAvailableAssessmentTypes = () => {
    const assessmentTypes = new Set();

    Object.values(studentAssessments).forEach(studentAssessmentList => {
      studentAssessmentList.forEach(assessment => {
        if (assessment.name) {
          assessmentTypes.add(assessment.name);
        }
      });
    });

    const sortedTypes = Array.from(assessmentTypes).sort();
    setAvailableAssessmentTypes(sortedTypes);
  };

  // Filter functions for search
  const getFilteredClasses = () => {
    if (!classSearchTerm.trim()) return classes;

    const searchLower = classSearchTerm.toLowerCase();
    return classes.filter(classItem => {
      const className = classItem.name?.toLowerCase() || '';
      const gradeLevel = classItem.grade_level?.toLowerCase() || '';
      const teacherName = classItem.teacherName?.toLowerCase() || '';

      return className.includes(searchLower) ||
        gradeLevel.includes(searchLower) ||
        teacherName.includes(searchLower);
    });
  };

  const getFilteredAssessmentTypes = () => {
    if (!assessmentSearchTerm.trim()) return availableAssessmentTypes;

    const searchLower = assessmentSearchTerm.toLowerCase();
    return availableAssessmentTypes.filter(assessmentType =>
      assessmentType.toLowerCase().includes(searchLower)
    );
  };

  const getFilteredStudentsWithAssessments = () => {
    let students = getStudentsWithAssessments();

    if (!studentSearchTerm.trim()) return students;

    const searchLower = studentSearchTerm.toLowerCase();
    return students.filter(student => {
      const studentName = student.name?.toLowerCase() || '';
      const studentGrade = student.grade?.toLowerCase() || '';
      const info = getStudentAssessmentInfo(student);
      const assessmentNames = info.assessmentNames.join(' ').toLowerCase();

      return studentName.includes(searchLower) ||
        studentGrade.includes(searchLower) ||
        assessmentNames.includes(searchLower);
    });
  };

  const getStudentsWithAssessments = () => {
    if (!selectedAssessmentType) {
      // Return all students who have any pending assessments
      return classStudents.filter(student => {
        const studentAssessmentList = studentAssessments[student.id] || [];
        return studentAssessmentList.length > 0;
      });
    }

    // Return students who have the selected assessment type
    return classStudents.filter(student => {
      const studentAssessmentList = studentAssessments[student.id] || [];
      return studentAssessmentList.some(assessment => assessment.name === selectedAssessmentType);
    });
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        // When deselecting a student, also deselect all their assessment instances
        const studentAssessmentList = studentAssessments[studentId] || [];
        const studentAssessmentIds = studentAssessmentList.map(a => a.id);
        setSelectedAssessmentIds(prevIds =>
          prevIds.filter(id => !studentAssessmentIds.includes(id))
        );
        return prev.filter(id => id !== studentId);
      } else {
        // When selecting a student, auto-select all their relevant assessment instances
        const studentAssessmentList = studentAssessments[studentId] || [];
        let relevantAssessments = studentAssessmentList;

        if (selectedAssessmentType) {
          relevantAssessments = studentAssessmentList.filter(a => a.name === selectedAssessmentType);
        }

        const newAssessmentIds = relevantAssessments.map(a => a.id);
        setSelectedAssessmentIds(prev => [...new Set([...prev, ...newAssessmentIds])]);

        return [...prev, studentId];
      }
    });
  };

  const handleAssessmentInstanceSelection = (studentId, assessmentId) => {
    setSelectedAssessmentIds(prev => {
      if (prev.includes(assessmentId)) {
        const newIds = prev.filter(id => id !== assessmentId);

        // If no assessment instances left for this student, deselect the student
        const studentAssessmentList = studentAssessments[studentId] || [];
        const studentHasSelectedAssessments = studentAssessmentList.some(a =>
          newIds.includes(a.id) && (!selectedAssessmentType || a.name === selectedAssessmentType)
        );

        if (!studentHasSelectedAssessments) {
          setSelectedStudents(prevStudents => prevStudents.filter(id => id !== studentId));
        }

        return newIds;
      } else {
        // If selecting an assessment instance, make sure the student is also selected
        setSelectedStudents(prevStudents => {
          if (!prevStudents.includes(studentId)) {
            return [...prevStudents, studentId];
          }
          return prevStudents;
        });

        return [...prev, assessmentId];
      }
    });
  };

  const handleSelectAllStudents = () => {
    const eligibleStudents = getFilteredStudentsWithAssessments();
    const eligibleStudentIds = eligibleStudents.map(student => student.id);

    // Check if all filtered students are selected
    const allFilteredSelected = eligibleStudentIds.every(id => selectedStudents.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered students and their assessment instances
      const assessmentIdsToRemove = [];
      eligibleStudentIds.forEach(studentId => {
        const studentAssessmentList = studentAssessments[studentId] || [];
        studentAssessmentList.forEach(assessment => {
          assessmentIdsToRemove.push(assessment.id);
        });
      });

      setSelectedStudents(prev => prev.filter(id => !eligibleStudentIds.includes(id)));
      setSelectedAssessmentIds(prev =>
        prev.filter(id => !assessmentIdsToRemove.includes(id))
      );
    } else {
      // Select all filtered students and their relevant assessment instances
      const newAssessmentIds = [];
      eligibleStudentIds.forEach(studentId => {
        const studentAssessmentList = studentAssessments[studentId] || [];
        let relevantAssessments = studentAssessmentList;

        if (selectedAssessmentType) {
          relevantAssessments = studentAssessmentList.filter(a => a.name === selectedAssessmentType);
        }

        relevantAssessments.forEach(assessment => {
          newAssessmentIds.push(assessment.id);
        });
      });

      setSelectedStudents(prev => {
        const newSelection = [...prev];
        eligibleStudentIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });

      setSelectedAssessmentIds(prev => [...new Set([...prev, ...newAssessmentIds])]);
    }
  };

  const getStudentAssessmentInfo = (student) => {
    const studentAssessmentList = studentAssessments[student.id] || [];

    if (!selectedAssessmentType) {
      return {
        hasAssessments: studentAssessmentList.length > 0,
        assessmentNames: studentAssessmentList.map(a => a.name),
        count: studentAssessmentList.length
      };
    }

    const matchingAssessments = studentAssessmentList.filter(a => a.name === selectedAssessmentType);
    return {
      hasAssessments: matchingAssessments.length > 0,
      assessmentNames: matchingAssessments.map(a => a.name),
      count: matchingAssessments.length,
      assessmentIds: matchingAssessments.map(a => a.id)
    };
  };

  const handleRemoveAssessments = async () => {
    try {
      setLoading(true);
      console.log('Removing assessment instances:', selectedAssessmentIds);

      // Direct API call - no splitting needed!
      await unassignAssessmentBatch(selectedAssessmentIds);

      const totalRemovals = selectedAssessmentIds.length;

      // Count affected students
      const affectedStudents = new Set();
      Object.entries(studentAssessments).forEach(([studentId, assessments]) => {
        if (assessments.some(a => selectedAssessmentIds.includes(a.id))) {
          affectedStudents.add(studentId);
        }
      });
      const studentCount = affectedStudents.size;

      setSuccess(`Successfully removed ${totalRemovals} assessment assignment${totalRemovals !== 1 ? 's' : ''} from ${studentCount} student${studentCount !== 1 ? 's' : ''}`);
      setShowConfirmModal(false);
      setSelectedStudents([]);
      setSelectedAssessmentIds([]);

      // Reload student assessments to reflect changes
      await loadStudentAssessments();
    } catch (err) {
      console.error('Error removing assessments:', err);
      setError("Failed to remove assessments from students");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedStudentDetails = () => {
    const removalsByStudent = {};

    // Find which students have the selected assessments
    Object.entries(studentAssessments).forEach(([studentId, assessments]) => {
      const studentSelectedAssessments = assessments.filter(a => selectedAssessmentIds.includes(a.id));
      if (studentSelectedAssessments.length > 0) {
        removalsByStudent[studentId] = studentSelectedAssessments;
      }
    });

    return Object.entries(removalsByStudent).map(([studentId, assessments], index) => {
      const student = classStudents.find(s => s.id === studentId);
      return {
        index: index + 1,
        name: student?.name || 'Unknown Student',
        grade: student?.grade || 'Unknown',
        assessmentsToRemove: assessments.length,
        assessmentNames: assessments.map(a => a.name).join(', ')
      };
    });
  };

  // Clear search function
  const clearSearch = (searchType) => {
    switch (searchType) {
      case 'class':
        setClassSearchTerm("");
        break;
      case 'assessment':
        setAssessmentSearchTerm("");
        break;
      case 'student':
        setStudentSearchTerm("");
        break;
    }
  };

  return (
    <Container fluid className="p-4">
      <div className="mb-4">
        <h3>Remove Screeners from Classes</h3>
        <p className="text-muted">School: <strong>{schoolName}</strong></p>
      </div>

      {/* Error and Success Messages */}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <Row>
        {/* Class Selection */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <h5><People className="me-2" />Select Class</h5>
            </Card.Header>
            <Card.Body>
              {/* Class Search */}
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search classes by name, grade, or teacher..."
                  value={classSearchTerm}
                  onChange={(e) => setClassSearchTerm(e.target.value)}
                />
                {classSearchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => clearSearch('class')}
                    size="sm"
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>

              <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {loading ? (
                  <div className="text-center p-3">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading classes...
                  </div>
                ) : getFilteredClasses().length === 0 ? (
                  <div>
                    {classSearchTerm ? (
                      <p className="text-muted">No classes found matching "{classSearchTerm}"</p>
                    ) : (
                      <p className="text-muted">No classes found for this school</p>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-2"
                      onClick={loadClasses}
                    >
                      Retry Loading Classes
                    </Button>
                  </div>
                ) : (
                  <>
                    {classSearchTerm && (
                      <small className="text-muted mb-2 d-block">
                        {getFilteredClasses().length} of {classes.length} classes shown
                      </small>
                    )}
                    {getFilteredClasses().map(classItem => (
                      <Card
                        key={classItem.id}
                        className={`mb-2 cursor-pointer ${selectedClass?.id === classItem.id ? 'border-primary' : ''}`}
                        onClick={() => setSelectedClass(classItem)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{classItem.name}</strong>
                              <br />
                              <small className="text-muted">Grade: {classItem.grade_level}</small>
                              <br />
                              <small className="text-muted">Students: {classItem.studentCount || 0}</small>
                              {classItem.teacherName && (
                                <>
                                  <br />
                                  <small className="text-muted">Teacher: {classItem.teacherName}</small>
                                </>
                              )}
                            </div>
                            {selectedClass?.id === classItem.id && (
                              <CheckCircle className="text-primary" />
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Assessment Type Filter */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <h5><Book className="me-2" />Filter by Assessment Type</h5>
            </Card.Header>
            <Card.Body>
              {!selectedClass ? (
                <p className="text-muted">Please select a class first</p>
              ) : loadingAssessments ? (
                <p>Loading assessment data...</p>
              ) : availableAssessmentTypes.length === 0 ? (
                <p className="text-muted">No pending assessments found in this class</p>
              ) : (
                <>
                  {/* Assessment Search */}
                  {availableAssessmentTypes.length > 3 && (
                    <InputGroup className="mb-3">
                      <InputGroup.Text>
                        <Search size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search assessment types..."
                        value={assessmentSearchTerm}
                        onChange={(e) => setAssessmentSearchTerm(e.target.value)}
                      />
                      {assessmentSearchTerm && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => clearSearch('assessment')}
                          size="sm"
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </InputGroup>
                  )}

                  <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    {/* All Assessment Types Option */}
                    <div className="mb-3">
                      <Card
                        className={`cursor-pointer ${!selectedAssessmentType ? 'border-warning' : ''}`}
                        onClick={() => setSelectedAssessmentType(null)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>All Assessment Types</strong>
                              <br />
                              <small className="text-muted">Remove all pending assessments</small>
                            </div>
                            {!selectedAssessmentType && (
                              <CheckCircle className="text-warning" />
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>

                    <h6 className="text-muted mb-2">Specific Assessment Types:</h6>
                    {assessmentSearchTerm && (
                      <small className="text-muted mb-2 d-block">
                        {getFilteredAssessmentTypes().length} of {availableAssessmentTypes.length} assessments shown
                      </small>
                    )}
                    {getFilteredAssessmentTypes().map(assessmentType => (
                      <Card
                        key={assessmentType}
                        className={`mb-2 cursor-pointer ${selectedAssessmentType === assessmentType ? 'border-danger' : ''}`}
                        onClick={() => setSelectedAssessmentType(assessmentType)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{assessmentType}</strong>
                              <br />
                              <small className="text-muted">
                                Students with this assessment: {
                                  classStudents.filter(student => {
                                    const studentAssessmentList = studentAssessments[student.id] || [];
                                    return studentAssessmentList.some(a => a.name === assessmentType);
                                  }).length
                                }
                              </small>
                            </div>
                            {selectedAssessmentType === assessmentType && (
                              <CheckCircle className="text-danger" />
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                    {getFilteredAssessmentTypes().length === 0 && assessmentSearchTerm && (
                      <p className="text-muted text-center">No assessments found matching "{assessmentSearchTerm}"</p>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Student Selection */}
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5><PersonDash className="me-2" />Select Students</h5>
                {selectedClass && (
                  <small className="text-muted">
                    {selectedStudents.length} of {getStudentsWithAssessments().length} selected
                  </small>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {!selectedClass ? (
                <p className="text-muted">Please select a class first</p>
              ) : loadingStudentData ? (
                <p>Loading students...</p>
              ) : loadingAssessments ? (
                <p>Loading student assessment data...</p>
              ) : getStudentsWithAssessments().length === 0 ? (
                <p className="text-muted">
                  {selectedAssessmentType ?
                    `No students found with "${selectedAssessmentType}" assessment` :
                    'No students found with pending assessments'
                  }
                </p>
              ) : (
                <>
                  {/* Student Search */}
                  <InputGroup className="mb-3">
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search students by name, grade, or assessment..."
                      value={studentSearchTerm}
                      onChange={(e) => setStudentSearchTerm(e.target.value)}
                    />
                    {studentSearchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => clearSearch('student')}
                        size="sm"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </InputGroup>

                  <div className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label={`Select All ${studentSearchTerm ? 'Filtered' : 'Eligible'} Students`}
                      checked={(() => {
                        const filtered = getFilteredStudentsWithAssessments();
                        const filteredIds = filtered.map(s => s.id);
                        return filteredIds.length > 0 && filteredIds.every(id => selectedStudents.includes(id));
                      })()}
                      onChange={handleSelectAllStudents}
                    />
                  </div>

                  {loadingAnswerCounts && (
                    <div className="text-center mb-3">
                      <small className="text-muted">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Loading answer counts...
                      </small>
                    </div>
                  )}

                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {studentSearchTerm && (
                      <small className="text-muted mb-2 d-block">
                        {getFilteredStudentsWithAssessments().length} of {getStudentsWithAssessments().length} students shown
                      </small>
                    )}
                    {getFilteredStudentsWithAssessments().length === 0 && studentSearchTerm ? (
                      <p className="text-muted text-center">No students found matching "{studentSearchTerm}"</p>
                    ) : (
                      getFilteredStudentsWithAssessments().map(student => {
                        const info = getStudentAssessmentInfo(student);
                        const studentAssessmentList = studentAssessments[student.id] || [];

                        // Filter assessments based on selected assessment type
                        let relevantAssessments = studentAssessmentList;
                        if (selectedAssessmentType) {
                          relevantAssessments = studentAssessmentList.filter(a => a.name === selectedAssessmentType);
                        }

                        return (
                          <div key={student.id} className="mb-3 p-3 border rounded">
                            {/* Student Header */}
                            <div className="d-flex align-items-center mb-2">
                              <Form.Check
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleStudentSelection(student.id)}
                                className="me-2"
                              />
                              <div className="flex-grow-1">
                                <strong>{student.name}</strong>
                                <br />
                                <small className="text-muted">Grade: {student.grade}</small>
                              </div>
                              <div>
                                <Badge bg="warning" text="dark">
                                  {relevantAssessments.length} assessment{relevantAssessments.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>

                            {/* Individual Assessment Checkboxes */}
                            {relevantAssessments.length > 0 && (
                              <div className="ms-4 ps-2 border-start border-2 border-light">
                                <small className="text-muted d-block mb-2">Select specific assessments to remove:</small>
                                {relevantAssessments.map(assessment => {
                                  const answerCount = assessmentAnswerCounts[assessment.id] || 0;
                                  const hasAnswers = answerCount > 0;

                                  return (
                                    <div key={assessment.id} className="mb-2">
                                      <Form.Check
                                        type="checkbox"
                                        id={`assessment-${student.id}-${assessment.id}`}
                                        checked={selectedAssessmentIds.includes(assessment.id)}
                                        onChange={() => handleAssessmentInstanceSelection(student.id, assessment.id)}
                                        label={
                                          <div className="w-100">
                                            <div className="d-flex justify-content-between align-items-start">
                                              <div className="flex-grow-1">
                                                <span className="fw-medium">{assessment.name}</span>
                                                {assessment.assignedDate && (
                                                  <small className="text-muted ms-2">
                                                    (Assigned: {new Date(assessment.assignedDate).toLocaleDateString()})
                                                  </small>
                                                )}
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                {/* Answer Count Badge */}
                                                <Badge 
                                                  bg={hasAnswers ? "info" : "secondary"} 
                                                  className="ms-2"
                                                >
                                                  {loadingAnswerCounts ? "..." : `${answerCount} answers`}
                                                </Badge>
                                                {hasAnswers && (
                                                  <Badge bg="warning" className="ms-1">
                                                    Progress
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>

                                            {/* Answer Count Information */}
                                            {hasAnswers && (
                                              <div className="mt-1 p-2 bg-light rounded small">
                                                <div className="text-info">
                                                  ✍️ {answerCount} answers submitted
                                                </div>
                                                <div className="mt-1">
                                                  <small className="text-warning">
                                                    ⚠️ Student has submitted answers - progress will be lost
                                                  </small>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        }
                                        className="small"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="text-center mt-4">
        <Button variant="secondary" onClick={onClose} className="me-2">
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => setShowConfirmModal(true)}
          disabled={!selectedClass || selectedAssessmentIds.length === 0 || getStudentsWithAssessments().length === 0}
        >
          <PersonDash className="me-2" />
          Remove Assessments ({selectedAssessmentIds.length} assessment{selectedAssessmentIds.length !== 1 ? 's' : ''})
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Trash className="me-2" />
            Confirm Assessment Removal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <ExclamationTriangle className="me-2" />
            <strong>Warning:</strong> You are about to remove <strong>{selectedAssessmentIds.length}</strong> assessment assignment{selectedAssessmentIds.length !== 1 ? 's' : ''} from students in <strong>{selectedClass?.name}</strong>.
            <br /><br />
            This action cannot be undone. Students will lose their progress on these specific assessments.
          </Alert>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Student Name</th>
                <th>Grade</th>
                <th>Assessment Count</th>
                <th>Specific Assessments to Remove</th>
                <th>Total Answers</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedStudentDetails().map((student) => {
                // Calculate total answer count for this student's selected assessments
                const studentAssessmentList = studentAssessments[student.index] || [];
                const selectedStudentAssessments = studentAssessmentList.filter(a => selectedAssessmentIds.includes(a.id));
                const totalAnswers = selectedStudentAssessments.reduce((sum, assessment) => {
                  return sum + (assessmentAnswerCounts[assessment.id] || 0);
                }, 0);

                return (
                  <tr key={student.index}>
                    <td>{student.index}</td>
                    <td>{student.name}</td>
                    <td>{student.grade}</td>
                    <td>{student.assessmentsToRemove}</td>
                    <td>
                      <small>{student.assessmentNames}</small>
                    </td>
                    <td>
                      <Badge bg={totalAnswers > 0 ? "warning" : "secondary"}>
                        {totalAnswers} answers
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemoveAssessments}
            disabled={loading}
          >
            {loading ? "Removing..." : "Confirm Removal"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ClassScreenerRemoval;